import React, { useCallback, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  useGridApiRef,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
} from '@mui/x-data-grid-premium';
import StyledDataGrid from '../../components/DataGrid/DataGridStyles';
import { useTableSearch } from '../../hooks/useTableSearch';
import AutosizeColumnsToggle from '../../components/DataGrid/AutosizeColumnsToggle';
import GroupToggle from '../../components/DataGrid/GroupToggle';
import Toolbar from '../../components/Toolbar/Toolbar';
import LayersIcon from '@mui/icons-material/Layers';
import SyncIcon from '@mui/icons-material/Sync';
import { useViewButtons } from '../../components/Views/useViewButtons';
import { useViewManagement } from '../../hooks/useViewManagement/useViewManagement';
import SaveViewDialog from '../../components/Views/SaveViewDialog';
import SavedViewsPanel from '../../components/Views/SavedViewsPanel';
import ActiveViewIndicator from '../../components/Views/ActiveViewIndicator';
import ViewContextMenu from '../../components/Views/ViewContextMenu';
import { useOTCDatabaseData } from './useOTCDatabaseData';
import { useOTCDatabaseColumns } from './useOTCDatabaseColumns';
import { OTCDatabaseRow } from './types';
import { PAGE_TYPES } from '../../constants/pageTypes';
import ManageGroupsModal from '../../components/Groups/ManageGroupsModal';
import {
  transformDataForGrouping,
  getTreeDataPath,
  convertGridColumnsToGroupColumns,
} from '../../components/Groups/groupUtils';
import { useGroupingColDef } from '../../components/Groups/useManageGroups';
import { createPinnedColumnsHandler } from '../../utils/gridPinnedColumnsHelper';
import { createStateChangeHandler } from '../../components/Groups/gridStateHelper';

export const OTCDatabasePage: React.FC = () => {
  const apiRef = useGridApiRef();

  // Get OTC database data
  const {
    otcData,
    loading,
    selectedRows,
    autosizeColumns,
    setAutosizeColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    handleExport,
    handleRowSelectionChange,
    // Sync
    isSyncingOTCDatabase,
    handleSyncOTCDatabase,
    // Groups
    groups,
    setGroups,
    isGroupsModalOpen,
    isGroupingEnabled,
    setIsGroupingEnabled,
    availableRowsForGroups,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
    // Row update
    processRowUpdate,
    handleProcessRowUpdateError,
    // Dynamic columns from wizard
    dynamicColumns,
  } = useOTCDatabaseData({
    apiRef,
    displayData: [],
  });

  const {
    filteredData: searchFilteredData,
    searchTerm,
    handleSearch,
  } = useTableSearch<OTCDatabaseRow>(otcData);

  // Add view management hook
  const viewManager = useViewManagement({
    pageType: PAGE_TYPES.OTC_DATABASE,
    apiRef,
    searchTerm,
    selectedRows,
    autosizeColumns,
    displayData: searchFilteredData,
    handleSearch,
    setSelectedRows: rows => {
      const model = { ids: new Set(rows), type: 'include' as const };
      handleRowSelectionChange(model);
    },
    setAutosizeColumns,
    isGroupingEnabled,
    setIsGroupingEnabled,
    groups,
    setGroups,
  });

  // Apply selected rows filtering when active view has selections
  const baseDisplayData = viewManager.filteredDisplayData as OTCDatabaseRow[];

  // Transform data for grouping
  const displayData = useMemo(() => {
    return transformDataForGrouping(baseDisplayData, groups, isGroupingEnabled);
  }, [baseDisplayData, groups, isGroupingEnabled]);

  // Override applyPinnedColumns to ensure protected columns are always first
  const originalApplyPinnedColumns = viewManager.applyPinnedColumns;
  viewManager.applyPinnedColumns = useCallback(
    createPinnedColumnsHandler(originalApplyPinnedColumns, isGroupingEnabled),
    [originalApplyPinnedColumns, isGroupingEnabled]
  );

  // Generate columns using the custom hook
  const { columns } = useOTCDatabaseColumns({
    autosizeColumns,
    isGroupingEnabled,
    dynamicColumns,
  });

  // Get grouping column definition
  const groupingColDef = useGroupingColDef(isGroupingEnabled);

  // Get view buttons from hook
  const viewButtons = useViewButtons({
    onSaveView: viewManager.openSaveDialog,
    onOpenSavedViews: viewManager.openSavedViewsPanel,
    loading: loading,
    disabled: false,
  });

  // Helper function to track view modifications
  const updateViewModifications = React.useCallback(() => {
    const modifications = viewManager.compareWithAppliedView();
    viewManager.setViewModifications(modifications);
  }, [viewManager]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 2,
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Toolbar
          onSearch={handleSearch}
          placeholder="Search OTC instruments..."
          searchTerm={searchTerm}
          disabled={loading}
          onExport={handleExport}
          buttons={[
            ...viewButtons,
            {
              variant: 'text',
              label: 'Sync from Illio',
              color: 'secondary',
              onClick: handleSyncOTCDatabase,
              icon: isSyncingOTCDatabase ? (
                <CircularProgress size={20} />
              ) : (
                <SyncIcon />
              ),
              disabled: loading || isSyncingOTCDatabase,
              tooltip: 'Synchronize OTC data from Illio',
            },
            {
              variant: 'text',
              label: 'Groups',
              color: 'secondary',
              onClick: handleOpenGroupsModal,
              icon: <LayersIcon />,
              disabled: loading,
              tooltip: 'Organize OTC instruments into groups',
            },
          ]}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutosizeColumnsToggle
              checked={autosizeColumns}
              onChange={setAutosizeColumns}
            />
            <GroupToggle
              checked={isGroupingEnabled}
              onChange={setIsGroupingEnabled}
              disabled={groups.length === 0}
            />
          </Box>
          {viewManager.activeView && (
            <ActiveViewIndicator
              view={viewManager.activeView}
              modifications={viewManager.viewModifications}
              onClose={viewManager.clearActiveView}
              onUpdateView={viewManager.updateCurrentView}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ height: 'calc(100vh - 330px)', width: '100%' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <StyledDataGrid
            key={isGroupingEnabled ? 'grouped-grid' : 'normal-grid'}
            apiRef={apiRef}
            rows={displayData}
            columns={columns}
            getRowId={row => row.id}
            getRowClassName={params =>
              params.row.isGroup ? 'group-row' : 'data-row'
            }
            checkboxSelection
            disableRowSelectionOnClick
            treeData={isGroupingEnabled}
            getTreeDataPath={isGroupingEnabled ? getTreeDataPath : undefined}
            groupingColDef={groupingColDef}
            defaultGroupingExpansionDepth={isGroupingEnabled ? 1 : undefined}
            columnVisibilityModel={columnVisibilityModel}
            initialState={{
              pinnedColumns: {
                left: [
                  GRID_CHECKBOX_SELECTION_COL_DEF.field,
                  ...(isGroupingEnabled
                    ? [GRID_TREE_DATA_GROUPING_FIELD, 'group_name']
                    : []),
                ],
              },
            }}
            onRowSelectionModelChange={handleRowSelectionChange}
            onSortModelChange={() => {
              updateViewModifications();
            }}
            onFilterModelChange={() => {
              updateViewModifications();
            }}
            onColumnVisibilityModelChange={newModel => {
              setColumnVisibilityModel(newModel);
              updateViewModifications();
            }}
            onPinnedColumnsChange={() => {
              if (viewManager.activeView) {
                requestAnimationFrame(() => {
                  updateViewModifications();
                });
              }
            }}
            onStateChange={createStateChangeHandler(apiRef, isGroupingEnabled)}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
          />
        )}
      </Box>

      <SaveViewDialog
        open={viewManager.isSaveViewDialogOpen}
        onClose={viewManager.closeSaveDialog}
        onSave={viewManager.saveView}
        editingView={viewManager.editingView}
        editMode={viewManager.editMode}
        currentSearchTerm={searchTerm}
        autosizeColumns={autosizeColumns}
        isGroupingEnabled={isGroupingEnabled}
        selectedRows={selectedRows}
        savedViews={viewManager.savedViews}
        columnFilters={viewManager.getColumnFilters()}
        sortModel={viewManager.getSortModel()}
        columnVisibility={viewManager.getColumnVisibility()}
        pinnedColumns={viewManager.getPinnedColumns()}
        columns={columns}
      />

      <SavedViewsPanel
        views={viewManager.savedViews}
        onViewClick={viewManager.handleViewClick}
        onSaveClick={viewManager.openSaveDialog}
        onMenuClick={viewManager.handleMenuClick}
        activeView={viewManager.activeView}
        open={viewManager.isSavedViewsPanelOpen}
        onClose={viewManager.closeSavedViewsPanel}
      />

      <ViewContextMenu
        anchorEl={viewManager.contextMenuAnchor}
        open={Boolean(viewManager.contextMenuAnchor)}
        onClose={viewManager.handleCloseContextMenu}
        onApply={viewManager.handleApplyView}
        onRename={viewManager.handleRenameView}
        onUpdate={viewManager.handleUpdateView}
        onDelete={viewManager.handleDeleteView}
      />

      <ManageGroupsModal
        open={isGroupsModalOpen}
        onClose={handleCloseGroupsModal}
        onSave={handleSaveGroups}
        availableRows={availableRowsForGroups}
        existingGroups={groups}
        title="Manage Groups"
        columns={useMemo(
          () => convertGridColumnsToGroupColumns(columns),
          [columns]
        )}
        hiddenColumns={['multiplier', 'group_name']}
      />
    </Box>
  );
};
